import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { TickingClock } from './TickingClock';
import type { Location } from '../types';

interface ClaimSharedLocationSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
  /**
   * BLU-367 claim-shadow — the org's own (non-shared) IMS locations. Used to
   * warn when the Shopify location the user is about to bind this claim to
   * already maps to an owned location: `getLocationUuid` checks owned
   * locations BEFORE the 3PL junction, so that owned location would silently
   * shadow this claim in order routing until its fulfilment is delegated.
   */
  ownLocations?: Location[];
}

const CLAIM_CODE_RAW_LENGTH = 16; // 4 segments × 4 chars

/**
 * Format a typed/pasted claim code into the canonical PREFIX-XXXX-XXXX-XXXX-XXXX
 * shape. The prefix is the run of letters BEFORE any digit or dash — this
 * handles raw paste like `LOCABCD2EFGH...` (where the prefix was meant to be
 * `LOC`) without arbitrarily slicing at 8 chars. If a dash is typed it is
 * preferred as the prefix delimiter.
 */
const formatClaimCodeInput = (value: string): string => {
  const upper = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
  if (!upper) return '';

  const dashIdx = upper.indexOf('-');
  let prefix = '';
  let tail = '';
  if (dashIdx >= 0) {
    prefix = upper.slice(0, dashIdx);
    tail = upper.slice(dashIdx + 1).replace(/-/g, '');
  } else {
    // No dash: prefix is the run of letters before the first digit. If the
    // input is letters-only (still typing the prefix), keep it as-is.
    const m = upper.match(/^([A-Z]+)([0-9].*)?$/);
    if (m) {
      prefix = m[1] ?? upper;
      tail = (m[2] ?? '').replace(/-/g, '');
    } else {
      // Starts with digits — treat the first 1-3 chars as prefix to keep
      // the input valid-looking until they finish typing.
      prefix = upper.slice(0, Math.min(3, upper.length));
      tail = upper.slice(prefix.length).replace(/-/g, '');
    }
  }
  if (!prefix) return '';
  // Cap prefix at 8 chars (matches generator).
  prefix = prefix.slice(0, 8);
  const tailSegments = tail.slice(0, CLAIM_CODE_RAW_LENGTH).match(/.{1,4}/g) ?? [];
  return tailSegments.length > 0 ? `${prefix}-${tailSegments.join('-')}` : prefix;
};

const CLAIM_CODE_REGEX = /^[A-Z0-9]+(?:-[A-Z0-9]{4}){4}$/;

export default function ClaimSharedLocationSheet({
  open,
  onOpenChange,
  onComplete,
  ownLocations = [],
}: ClaimSharedLocationSheetProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [claimCode, setClaimCode] = useState('');
  const [selectedShopifyId, setSelectedShopifyId] = useState<number | null>(null);
  const [shouldFetchShopify, setShouldFetchShopify] = useState(false);
  // BLU-389 Phase D — manual fallback when Shopify-API listing isn't usable
  // (no creds yet on the test/staging client org, or Shopify is rate-limiting).
  // Backend `claimSharedLocation` re-validates ownership server-side: one of
  // the org's own locations carries the id, or (BLU-1367) a live check finds
  // it ACTIVE in the org's own Shopify store. So the manual path is UI-only —
  // it can't grant access to a Shopify ID the caller doesn't hold.
  const [manualMode, setManualMode] = useState(false);
  const [manualShopifyId, setManualShopifyId] = useState('');
  // Staging-only escape hatch — when set, the picker SHOWS the manual input
  // alongside the Shopify list so test orgs without Shopify creds can complete
  // the flow without first triggering the error toast.
  const shopifyOptional = (import.meta.env.VITE_SHOPIFY_OPTIONAL ?? '') === 'true';

  const codeIsValid = CLAIM_CODE_REGEX.test(claimCode);
  const manualIdAsNumber = manualShopifyId.match(/^\d{1,15}$/) ? Number(manualShopifyId) : null;
  const chosenShopifyId = selectedShopifyId ?? manualIdAsNumber;
  // BLU-367 claim-shadow — the chosen Shopify location already maps to one of
  // the org's OWN locations. getLocationUuid checks owned locations before
  // the 3PL junction, so that own location would silently shadow this claim
  // until its fulfilment is delegated. Non-blocking — just a heads-up.
  const shadowingOwnLocation =
    chosenShopifyId != null
      ? ownLocations.find((loc) => loc.shopify_location_id === chosenShopifyId) ?? null
      : null;

  const shopifyLocationsQuery = trpc.locations.listShopifyLocations.useQuery(undefined, {
    enabled: shouldFetchShopify,
    staleTime: 5 * 60_000,
  });

  const claimMutation = trpc.locations.claimSharedLocation.useMutation({
    onSuccess: () => {
      setStep(3);
    },
    onError: (err) => {
      toast({
        title: 'Could not submit claim',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const reset = () => {
    setStep(1);
    setClaimCode('');
    setSelectedShopifyId(null);
    setShouldFetchShopify(false);
    setManualMode(false);
    setManualShopifyId('');
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
    if (!next && step === 3) onComplete?.();
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Claim a 3PL location</SheetTitle>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {step} of 3
            </span>
          </div>
          <SheetDescription>
            Connect your Shopify store to a partner warehouse so they can fulfil your orders.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="claim-code">Claim code</Label>
                <Input
                  id="claim-code"
                  className="font-mono text-lg tracking-[0.2em] uppercase mt-1.5"
                  placeholder="LOC-XXXX-XXXX-XXXX-XXXX"
                  value={claimCode}
                  onChange={(e) => setClaimCode(formatClaimCodeInput(e.target.value))}
                  autoFocus
                  aria-invalid={claimCode.length > 0 && !codeIsValid}
                />
                {claimCode.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Codes look like <span className="font-mono">PREFIX-XXXX-XXXX-XXXX-XXXX</span>. Ask your 3PL provider for theirs.
                  </p>
                ) : codeIsValid ? (
                  <p className="text-xs text-emerald-700 mt-1.5">
                    Looks right. Continue when you&rsquo;re ready.
                  </p>
                ) : (
                  <p className="text-xs text-amber-700 mt-1.5">
                    That doesn&rsquo;t look like a complete code yet. Keep going.
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <Alert>
                <AlertDescription>
                  You&rsquo;re about to connect to a partner warehouse. Once approved, your orders routed to the Shopify location you pick will be fulfilled by their staff.
                </AlertDescription>
              </Alert>

              <div>
                <Label>Which of your Shopify locations should we bind to this warehouse?</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Order items routed to this Shopify location will appear in the partner&rsquo;s queue.
                </p>

                <div className="mt-3 space-y-3">
                  {shopifyLocationsQuery.isLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                    </div>
                  ) : shopifyLocationsQuery.error || manualMode || shopifyOptional ? (
                    // BLU-389 Phase D fallback path. Renders when:
                    //   - Shopify API errored (no creds, rate limit, network),
                    //   - User explicitly switched to manual entry, OR
                    //   - VITE_SHOPIFY_OPTIONAL=true (staging-only).
                    // Server-side ownership validation still applies — the
                    // backend will reject any ID that is neither on one of the
                    // caller's own locations nor an active location in the
                    // caller's own Shopify store.
                    <div className="space-y-3">
                      {shopifyLocationsQuery.error && (
                        <Alert variant="destructive">
                          <AlertDescription>
                            We couldn&rsquo;t reach Shopify to list your locations. You can still continue by entering the Shopify location ID manually below.
                          </AlertDescription>
                        </Alert>
                      )}
                      <div className="rounded-lg border p-3">
                        <Label htmlFor="manual-shopify-id" className="font-medium">
                          Manually enter the Shopify location ID
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Your 3PL partner will only approve this if it matches a real location in your Shopify Admin.
                        </p>
                        <Input
                          id="manual-shopify-id"
                          type="text"
                          inputMode="numeric"
                          pattern="\d*"
                          autoComplete="off"
                          spellCheck={false}
                          className="font-mono mt-2"
                          placeholder="e.g. 84727890123"
                          value={manualShopifyId}
                          onChange={(e) =>
                            setManualShopifyId(e.target.value.replace(/[^0-9]/g, '').slice(0, 15))
                          }
                          aria-invalid={manualShopifyId.length > 0 && manualIdAsNumber == null}
                        />
                        {manualShopifyId.length > 0 && manualIdAsNumber == null && (
                          <p className="text-xs text-amber-700 mt-1.5">
                            Shopify location IDs are numeric only.
                          </p>
                        )}
                        {manualShopifyId.length > 0 &&
                          manualIdAsNumber != null &&
                          manualShopifyId.length < 8 && (
                            <p className="text-xs text-muted-foreground mt-1.5">
                              Shopify location IDs are typically 10 to 13 digits. Double-check before submitting.
                            </p>
                          )}
                      </div>
                      {/* Allow flipping back to the picker if Shopify is up but the user opened manual mode */}
                      {!shopifyLocationsQuery.error &&
                        manualMode &&
                        (shopifyLocationsQuery.data?.locations?.length ?? 0) > 0 && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 px-1 -mx-1"
                            onClick={() => {
                              setManualMode(false);
                              setManualShopifyId('');
                            }}
                          >
                            <span aria-hidden="true">←</span>
                            <span>Use the Shopify location picker instead</span>
                          </button>
                        )}
                    </div>
                  ) : (
                    <>
                      <RadioGroup
                        value={selectedShopifyId != null ? String(selectedShopifyId) : ''}
                        onValueChange={(v) => setSelectedShopifyId(Number(v))}
                        className="space-y-2"
                      >
                        {(shopifyLocationsQuery.data?.locations ?? []).map((loc) => (
                          <Label
                            key={loc.shopifyLocationId}
                            htmlFor={`shopify-${loc.shopifyLocationId}`}
                            className="flex items-center justify-between rounded-lg border p-3 cursor-pointer hover:bg-muted/40 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                          >
                            <div className="flex items-center gap-3">
                              <RadioGroupItem
                                id={`shopify-${loc.shopifyLocationId}`}
                                value={String(loc.shopifyLocationId)}
                              />
                              <div>
                                <div className="font-medium">{loc.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {loc.city || '-'}
                                  {loc.alreadyLinked ? ' · already linked' : ''}
                                </div>
                              </div>
                            </div>
                            <span className="text-xs font-mono text-muted-foreground">
                              ID {loc.shopifyLocationId}
                            </span>
                          </Label>
                        ))}
                        {(shopifyLocationsQuery.data?.locations ?? []).length === 0 && (
                          <p className="text-sm text-muted-foreground py-4 text-center">
                            No Shopify locations found. Create one in Shopify Admin first.
                          </p>
                        )}
                      </RadioGroup>
                      {/* Escape hatch: switch to manual entry when the picker
                          isn't showing the location they need (e.g. recently
                          created in Shopify and not yet synced). */}
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline mt-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 px-1 -mx-1"
                        onClick={() => setManualMode(true)}
                      >
                        Don&rsquo;t see your location? Enter the ID manually
                      </button>
                    </>
                  )}
                </div>
              </div>

              {shadowingOwnLocation && (
                <Alert variant="warning">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Your location &ldquo;{shadowingOwnLocation.name}&rdquo; is mapped to this
                    Shopify location. Orders will keep routing to it until you delegate its
                    fulfilment to this 3PL after the claim is approved.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col items-center justify-center py-10 text-center space-y-4">
              <TickingClock />
              <div>
                <h3 className="text-lg font-medium">Sent for approval.</h3>
                <p className="text-sm text-muted-foreground mt-1.5 max-w-sm">
                  Your 3PL partner will review and approve your access shortly. We&rsquo;ll notify you in-app when it&rsquo;s ready. You can close this now; nothing else to do.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="border-t pt-4 flex justify-end gap-2">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button
                disabled={!codeIsValid}
                onClick={() => {
                  setShouldFetchShopify(true);
                  setStep(2);
                }}
              >
                Continue
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                disabled={
                  claimMutation.isPending ||
                  // Either source must yield a valid numeric ID.
                  (selectedShopifyId == null && manualIdAsNumber == null)
                }
                onClick={() => {
                  const shopifyId = selectedShopifyId ?? manualIdAsNumber;
                  if (shopifyId == null) return;
                  claimMutation.mutate({
                    claimCode,
                    clientShopifyLocationId: shopifyId,
                  });
                }}
              >
                {claimMutation.isPending ? 'Submitting…' : 'Submit claim'}
              </Button>
            </>
          )}
          {step === 3 && (
            <Button onClick={() => handleClose(false)}>Done</Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
